import React, { useState } from "react";
import {
  IconButton,
  Popper,
  ClickAwayListener,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import RoomIcon from "@material-ui/icons/Room";
import GetAppIcon from "@material-ui/icons/GetApp";
import PrintIcon from "@material-ui/icons/Print";
import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BenchmarkPopover from "../../pages/publicMap/components/BenchmarkPopover";
import ScorecardCell from "./ScorecardCell";
import SeasonDetailPopover from "./SeasonDetailPopover";
import { NO_PRINT_CLASS } from "../../pages/wqScorecard/scorecardPrint";
import {
  STANDARD_BADGE,
  STANDARD_CAPTION,
} from "../../pages/wqScorecard/scorecardCopy";
import { formatValue } from "../../pages/wqScorecard/buildScorecardTable";

// Every parameter column is this wide, so the grid reads as a grid. Wide
// enough for a four-digit value and a trend glyph; past about a dozen columns
// the table scrolls rather than squeezing them narrower.
const DATA_COLUMN_WIDTH = 76;
const SITE_COLUMN_WIDTH = 112;

// Above MUI's tooltip layer (1500). A popover opens from a button inside a
// cell that has a hover tooltip, and the pointer is still on that cell when
// it opens, so anything lower is drawn underneath the tooltip.
const POPOVER_Z_INDEX = 1501;

const useStyles = makeStyles((theme) => ({
  container: {
    // Horizontal scroll on narrow screens rather than a squeezed table.
    overflowX: "auto",
  },
  table: {
    // Fixed layout so column widths come from the colgroup below rather than
    // from how long each header happens to be. Without it "NO3+NO2" and
    // "Water Temp" claim far more room than "TN" and the grid reads unevenly.
    tableLayout: "fixed",
  },
  caption: {
    captionSide: "top",
    textAlign: "left",
    padding: theme.spacing(1, 0),
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
  siteHeader: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: theme.palette.background.paper,
  },
  siteCell: {
    position: "sticky",
    left: 0,
    zIndex: 1,
    background: theme.palette.background.paper,
    fontWeight: 500,
    // Wrap rather than overflow: "CCR Composite" needs two lines at this
    // column width, and the column is fixed so the grid stays a grid.
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.2,
    padding: theme.spacing(0.5, 1),
    fontSize: "0.85rem",
  },
  // Name on the left, map button pinned to the right edge, so the buttons
  // form one straight column down the table instead of trailing each name.
  siteInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    width: "100%",
  },
  siteName: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  // A derived site (the reservoir composite) reads as a summary, not a station.
  compositeSite: {
    fontStyle: "italic",
  },
  // Explore mode, so this is safe from any period; see the page's getRowHref.
  // Outlined so it reads as a button at rest, not as a decorative pin.
  flyToButton: {
    padding: 2,
    fontSize: "1rem",
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.3)",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
    "&:hover, &:focus": {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  reachRow: {
    background: theme.palette.action.hover,
  },
  // The reach label lives in the site column, not in a cell spanning the
  // table, so it pins to the left edge exactly as the site names do when the
  // grid scrolls sideways. The filler cell beside it carries the row's tint
  // across the remaining width.
  reachCell: {
    position: "sticky",
    left: 0,
    zIndex: 1,
    padding: theme.spacing(0.5, 1),
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontSize: "0.75rem",
    whiteSpace: "normal",
    lineHeight: 1.2,
    background: theme.palette.action.hover,
  },
  reachFiller: {
    padding: 0,
    background: theme.palette.action.hover,
  },
  paramHeader: {
    textAlign: "center",
    borderLeft: "1px solid rgba(224, 224, 224, 1)",
    verticalAlign: "bottom",
    padding: theme.spacing(0.75, 0.5),
    fontSize: "0.82rem",
    // Long abbreviations wrap inside their column instead of widening it.
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  // The rule between the regulatory columns and the rest; same weight as the
  // one ScorecardCell draws on the body cells beneath it.
  groupDivider: {
    borderLeft: "3px solid rgba(0, 0, 0, 0.35)",
  },
  abbrev: {
    display: "block",
    lineHeight: 1.2,
  },
  // Reserved whether or not there is a button, so every header is the same
  // height and the abbreviations line up across the row.
  infoRow: {
    display: "block",
    minHeight: 18,
    lineHeight: 0,
  },
  units: {
    display: "block",
    fontWeight: 400,
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
  },
  // Marks a column judged against a regulatory standard rather than shaded
  // on the benchmark bands. Lives in the info-row slot so the header keeps
  // its height.
  standardBadge: {
    display: "inline-block",
    lineHeight: 1.4,
    fontSize: "0.58rem",
    fontWeight: 600,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    // The header allows mid-word breaks so long abbreviations fit; the badge
    // must not inherit that or "Standard" splits across two lines.
    whiteSpace: "nowrap",
    wordBreak: "normal",
    padding: "0 4px",
    borderRadius: 3,
    border: `1px solid ${theme.palette.text.secondary}`,
    color: theme.palette.text.secondary,
    cursor: "help",
  },
  infoButton: {
    padding: 2,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

/**
 * The crosstab: sites down, parameters across, grouped by stream reach, with
 * the reservoir as the last reach when it is included.
 *
 * Built from plain table elements rather than material-table because the shape
 * is a matrix, not a list: it needs reach group rows spanning the width, a
 * sticky site column, and every data cell individually focusable. Semantic
 * <th scope> markup also lets a screen reader announce the site and parameter
 * for whichever cell has focus.
 */
export default function ScorecardTable({
  table,
  benchmarkLineByParameter,
  periodLabel,
  getCellHref,
  getRowHref,
  onDownloadCsv,
  onPrint,
  emptyMessage,
}) {
  const classes = useStyles();
  const [popover, setPopover] = useState({ anchor: null, parameterid: null });
  const [seasonPopover, setSeasonPopover] = useState({
    anchor: null,
    cell: null,
  });

  const { columns, rows, statisticLabel, flowLabel, hasStandardColumns } =
    table;

  if (!columns.length || !rows.length) {
    return (
      <Typography className={classes.empty}>
        {emptyMessage ?? "Nothing to show."}
      </Typography>
    );
  }

  const openPopover = (event, parameterid) =>
    setPopover({ anchor: event.currentTarget, parameterid });
  const closePopover = () => setPopover({ anchor: null, parameterid: null });

  const popoverData = popover.parameterid
    ? benchmarkLineByParameter.get(popover.parameterid)
    : null;

  const openSeasons = (event, cell) =>
    setSeasonPopover({ anchor: event.currentTarget, cell });
  const closeSeasons = () => setSeasonPopover({ anchor: null, cell: null });

  // Group rows by reach so each stream gets a header row above its sites.
  const groups = [];
  rows.forEach((row) => {
    const last = groups[groups.length - 1];
    if (last && last.label === row.reachLabel) last.rows.push(row);
    else groups.push({ label: row.reachLabel, rows: [row] });
  });

  // The first ordinary column after the regulatory ones carries the divider.
  const dividerKey = hasStandardColumns
    ? columns.find((c) => !c.isStandard)?.key ?? null
    : null;

  return (
    <>
      {(onDownloadCsv || onPrint) && (
        <div className={`${classes.actions} ${NO_PRINT_CLASS}`}>
          {onDownloadCsv && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={onDownloadCsv}
            >
              Download CSV
            </Button>
          )}
          {onPrint && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={onPrint}
            >
              Print
            </Button>
          )}
        </div>
      )}
      <TableContainer className={classes.container}>
        <Table
          className={classes.table}
          size="small"
          style={{
            minWidth: SITE_COLUMN_WIDTH + columns.length * DATA_COLUMN_WIDTH,
          }}
        >
          <colgroup>
            <col style={{ width: SITE_COLUMN_WIDTH }} />
            {columns.map((column) => (
              <col key={column.key} style={{ width: DATA_COLUMN_WIDTH }} />
            ))}
          </colgroup>
          <caption className={classes.caption}>
            {`${statisticLabel} by site and parameter, ${flowLabel.toLowerCase()}`}
            {periodLabel ? `, ${periodLabel}.` : "."}
            {
              " Shading shows the CCBWQA benchmark band; the arrow shows the Mann-Kendall trend."
            }
            {hasStandardColumns ? ` ${STANDARD_CAPTION}` : ""}
          </caption>

          <TableHead>
            <TableRow>
              <TableCell
                component="th"
                scope="col"
                className={classes.siteHeader}
              >
                Site
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  component="th"
                  scope="col"
                  className={`${classes.paramHeader}${
                    column.key === dividerKey ? ` ${classes.groupDivider}` : ""
                  }`}
                >
                  {/* Three stacked lines, always in the same order and the
                      same places, so the headers read as one row rather
                      than a ragged one. The third line holds the info button
                      for a benchmark column or the badge for a standard
                      column, and its slot is reserved either way, so nothing
                      shifts. */}
                  <Tooltip title={column.description ?? ""}>
                    <span className={classes.abbrev}>{column.abbrev}</span>
                  </Tooltip>
                  <span className={classes.units}>
                    {column.isStandard && column.standard
                      ? `${column.units} · ≤ ${formatValue(
                          column.standard.value,
                          column.units
                        )}`
                      : column.units}
                  </span>
                  <span className={`${classes.infoRow} ${NO_PRINT_CLASS}`}>
                    {column.isStandard ? (
                      <Tooltip
                        title={column.standard?.basis ?? STANDARD_BADGE.hint}
                      >
                        <span
                          className={classes.standardBadge}
                          tabIndex={0}
                          aria-label={`${column.description}: ${
                            column.standard?.basis ?? STANDARD_BADGE.hint
                          }`}
                        >
                          {column.standard?.isInterim
                            ? STANDARD_BADGE.interim
                            : STANDARD_BADGE.label}
                        </span>
                      </Tooltip>
                    ) : (
                      benchmarkLineByParameter.has(column.parameterid) && (
                        <Tooltip
                          title={`Benchmark bands for ${column.description}`}
                        >
                          <IconButton
                            className={classes.infoButton}
                            size="small"
                            aria-label={`Benchmark bands for ${column.description}`}
                            onClick={(event) =>
                              openPopover(event, column.parameterid)
                            }
                          >
                            <InfoOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {groups.map((group) => (
              <React.Fragment key={group.label}>
                <TableRow className={classes.reachRow}>
                  <TableCell
                    component="th"
                    scope="rowgroup"
                    className={classes.reachCell}
                  >
                    {group.label}
                  </TableCell>
                  <TableCell
                    colSpan={columns.length}
                    className={classes.reachFiller}
                    aria-hidden="true"
                  />
                </TableRow>

                {group.rows.map((row) => (
                  <TableRow key={row.locationid} hover>
                    <TableCell
                      component="th"
                      scope="row"
                      className={classes.siteCell}
                    >
                      <span className={classes.siteInner}>
                        <Tooltip title={row.siteDescription ?? ""}>
                          <span
                            className={`${classes.siteName}${
                              row.isComposite ? ` ${classes.compositeSite}` : ""
                            }`}
                          >
                            {row.siteName}
                          </span>
                        </Tooltip>
                        {getRowHref && getRowHref(row) && (
                          <Tooltip
                            title={`Center the map on ${row.siteName} (opens in a new tab)`}
                          >
                            <IconButton
                              className={`${classes.flyToButton} ${NO_PRINT_CLASS}`}
                              size="small"
                              component="a"
                              href={getRowHref(row)}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Center the map on ${row.siteName}, ${
                                row.siteDescription ?? ""
                              } (opens in a new tab)`}
                            >
                              <RoomIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </span>
                    </TableCell>

                    {columns.map((column) => (
                      <ScorecardCell
                        key={column.key}
                        cell={row.cells[column.key]}
                        groupDivider={column.key === dividerKey}
                        href={
                          getCellHref
                            ? getCellHref(row.cells[column.key])
                            : null
                        }
                        onOpenSeasons={column.isStandard ? openSeasons : null}
                      />
                    ))}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>

        {popoverData && (
          <ClickAwayListener onClickAway={closePopover}>
            <Popper
              open={Boolean(popover.anchor)}
              anchorEl={popover.anchor}
              placement="bottom"
              style={{ zIndex: POPOVER_Z_INDEX }}
            >
              <BenchmarkPopover data={popoverData} />
            </Popper>
          </ClickAwayListener>
        )}

        {seasonPopover.cell && (
          <ClickAwayListener onClickAway={closeSeasons}>
            <Popper
              open={Boolean(seasonPopover.anchor)}
              anchorEl={seasonPopover.anchor}
              placement="bottom"
              style={{ zIndex: POPOVER_Z_INDEX }}
            >
              <SeasonDetailPopover
                cell={seasonPopover.cell}
                onClose={closeSeasons}
              />
            </Popper>
          </ClickAwayListener>
        )}
      </TableContainer>
    </>
  );
}
